import {
  type MemberCountryCode,
  normalizeMemberCountryCode,
} from "../location/memberLocation";

export type SupportCardTaxonomyRecord = {
  section?: string | null;
  category?: string | null;
  country_scope?: string | null;
};

export type SupportCardCountryScope = "ALL" | MemberCountryCode;
export const LIVE_SUPPORT_COUNTRY_CODES: MemberCountryCode[] = ["GB", "AU", "NZ", "US"];
export const COMING_SOON_SUPPORT_COUNTRY_CODES: MemberCountryCode[] = [];
export const PAUSED_SUPPORT_COUNTRY_CODES: MemberCountryCode[] = ["IE", "CA"];

export const SUPPORT_LEGACY_SECTION = "support";
export const SUPPORT_CANONICAL_SECTION = "support-hub";
export const SUPPORT_CARD_COUNTRY_SCOPE_OPTIONS: Array<{
  value: SupportCardCountryScope;
  label: string;
}> = [
  { value: "GB", label: "UK (incl. Northern Ireland)" },
  { value: "IE", label: "Republic of Ireland" },
  { value: "AU", label: "Australia" },
  { value: "NZ", label: "New Zealand" },
  { value: "US", label: "United States" },
  { value: "ALL", label: "Shared" },
];

export const SUPPORT_CATEGORY_NAME_BY_SLUG: Record<string, string> = {
  "money-benefits": "Money & Benefits",
  housing: "Housing",
  legal: "Legal & Rights",
  wellbeing: "Wellbeing",
  "education-send": "Education, Childcare & Additional Needs",
  work: "Work",
};

const SUPPORT_CATEGORY_ALIASES_BY_SLUG: Record<string, string[]> = {
  "money-benefits": ["Money & Benefits"],
  housing: ["Housing"],
  legal: ["Legal & Rights"],
  wellbeing: ["Wellbeing"],
  "education-send": [
    "Education, Childcare & Additional Needs",
    "Education & SEND",
  ],
  work: ["Work"],
};

const SUPPORT_LEGACY_CATEGORY_SET = new Set(Object.keys(SUPPORT_CATEGORY_NAME_BY_SLUG));
const SUPPORT_CANONICAL_CATEGORY_SET = new Set(
  Object.values(SUPPORT_CATEGORY_ALIASES_BY_SLUG).flat(),
);

export const getCanonicalSupportCategory = (slug: string) =>
  SUPPORT_CATEGORY_NAME_BY_SLUG[slug] ?? slug;

export const getSupportCategoryAliases = (slug: string) =>
  SUPPORT_CATEGORY_ALIASES_BY_SLUG[slug] ?? [getCanonicalSupportCategory(slug)];

export const normalizeSupportCardCountryScope = (
  value?: string | null,
): SupportCardCountryScope => {
  if (value === "GB" || value === "IE" || value === "AU" || value === "NZ" || value === "US") {
    return value;
  }

  return "ALL";
};

export const getSupportCardCountryScopeLabel = (value?: string | null) => {
  const normalized = normalizeSupportCardCountryScope(value);
  return (
    SUPPORT_CARD_COUNTRY_SCOPE_OPTIONS.find(
      (option) => option.value === normalized,
    )?.label ?? "Shared"
  );
};

export const getDefaultSupportCardCountryScope = (
  section = SUPPORT_CANONICAL_SECTION,
): SupportCardCountryScope =>
  section === "helper-packs" ? "ALL" : "GB";

export const hasLiveSupportForCountry = (countryCode?: string | null) => {
  if (!countryCode) {
    return true;
  }

  const normalized = normalizeMemberCountryCode(countryCode);
  return LIVE_SUPPORT_COUNTRY_CODES.includes(normalized);
};

export const isSupportComingSoonCountry = (countryCode?: string | null) => {
  if (!countryCode) {
    return false;
  }

  const normalized = normalizeMemberCountryCode(countryCode);
  return COMING_SOON_SUPPORT_COUNTRY_CODES.includes(normalized);
};

export const isSupportPausedCountry = (countryCode?: string | null) => {
  if (!countryCode) {
    return false;
  }

  const normalized = normalizeMemberCountryCode(countryCode);
  return PAUSED_SUPPORT_COUNTRY_CODES.includes(normalized);
};

export const matchesSupportCategory = (
  card: SupportCardTaxonomyRecord,
  slug: string,
) => {
  const canonicalCategories = getSupportCategoryAliases(slug);

  return Boolean(
    (card.section === SUPPORT_CANONICAL_SECTION &&
      card.category &&
      canonicalCategories.includes(card.category)) ||
      (card.section === SUPPORT_LEGACY_SECTION && card.category === slug),
  );
};

export const matchesSupportCardCountry = (
  card: SupportCardTaxonomyRecord,
  memberCountryCode?: string | null,
) => {
  const scope = normalizeSupportCardCountryScope(card.country_scope);
  if (scope === "ALL") {
    return true;
  }

  return scope === normalizeMemberCountryCode(memberCountryCode);
};

export const isSupportCard = (card: SupportCardTaxonomyRecord) =>
  Boolean(
    (card.section === SUPPORT_CANONICAL_SECTION &&
      card.category &&
      SUPPORT_CANONICAL_CATEGORY_SET.has(card.category)) ||
      (card.section === SUPPORT_LEGACY_SECTION &&
        card.category &&
        SUPPORT_LEGACY_CATEGORY_SET.has(card.category)),
  );

export const getDefaultSupportCardTaxonomy = (slug = "money-benefits") => ({
  section: SUPPORT_CANONICAL_SECTION,
  category: getCanonicalSupportCategory(slug),
  country_scope: getDefaultSupportCardCountryScope(SUPPORT_CANONICAL_SECTION),
});
